import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api } from '../api/client';
import type { Staff, StaffRole } from '../api/types';

/**
 * Session state for the console.
 *
 * The session lives in an httpOnly cookie the JavaScript cannot read, which is
 * the point — a token in localStorage is readable by any script that gets onto
 * the page. So "am I signed in?" is answered by asking the server once at
 * boot, not by inspecting storage.
 */

interface AuthValue {
  staff: Staff | null;
  status: 'checking' | 'signed-in' | 'signed-out';
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [status, setStatus] = useState<AuthValue['status']>('checking');
  const queryClient = useQueryClient();

  useEffect(() => {
    let cancelled = false;

    api
      .get<{ staff: Staff }>('/api/staff/auth/me')
      .then((data) => {
        if (cancelled) return;
        setStaff(data.staff);
        setStatus('signed-in');
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // A 401 here is the normal "not signed in yet" case, not a failure
        // worth reporting.
        if (!(error instanceof ApiError) || error.status !== 401) {
          console.error('[auth] session check failed', error);
        }
        setStatus('signed-out');
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      const data = await api.post<{ staff: Staff }>('/api/staff/auth/login', { email, password });
      setStaff(data.staff);
      setStatus('signed-in');
    },
    [],
  );

  const signOut = useCallback(async () => {
    try {
      await api.post('/api/staff/auth/logout');
    } finally {
      setStaff(null);
      setStatus('signed-out');
      // Otherwise the next person to sign in briefly sees the previous
      // session's companies from cache.
      queryClient.clear();
    }
  }, [queryClient]);

  const value = useMemo<AuthValue>(
    () => ({ staff, status, signIn, signOut }),
    [staff, status, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/**
 * What a role is allowed to do, in one place. The server enforces all of this
 * too (middleware on every /api/staff route) — these flags only decide which
 * buttons and pages to show, so the UI never dangles an action that would 403.
 *
 *   admin       everything
 *   deployment  create/edit, but never delete or revoke
 *   support     read-only, and only Overview, Companies and Licenses
 */
export interface StaffCapabilities {
  role: StaffRole | undefined;
  /** Create and edit (companies, licences, logins, products, options, sources, secrets). */
  canWrite: boolean;
  /** Delete and revoke. */
  canDestroy: boolean;
  /** Manage staff accounts, reset demo data. */
  isAdmin: boolean;
  /** Read-only support: show only Overview, Companies, Licenses. */
  isSupport: boolean;
}

export function capabilitiesFor(role: StaffRole | undefined): StaffCapabilities {
  return {
    role,
    canWrite: role === 'admin' || role === 'deployment',
    canDestroy: role === 'admin',
    isAdmin: role === 'admin',
    isSupport: role === 'support',
  };
}

export function useCapabilities(): StaffCapabilities {
  const { staff } = useAuth();
  return capabilitiesFor(staff?.role);
}
